/* eslint-disable react-refresh/only-export-components -- co-located helpers/constants */
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, Stack, Typography } from '@mui/material';
import { AppTextField } from '../design-system';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { SxProps, Theme } from '@mui/material/styles';
import type { Tenant } from '../../services/apiTypes';

export interface SearchableTenantOption {
  id: string;
  name: string;
  subdomain: string;
}

export function tenantToSearchableOption(t: Tenant): SearchableTenantOption {
  return { id: String(t.id), name: t.name, subdomain: t.subdomain ?? '' };
}

export interface SearchableTenantComboboxProps {
  options: SearchableTenantOption[];
  value: string;
  onChange: (tenantId: string) => void;
  loading?: boolean;
  label?: string;
  sx?: SxProps<Theme>;
  showSubdomainInOptions?: boolean;
  disabled?: boolean;
}

export function SearchableTenantCombobox({
  options,
  value,
  onChange,
  loading = false,
  label = 'Tenant',
  sx,
  showSubdomainInOptions = false,
  disabled = false,
}: SearchableTenantComboboxProps) {
  const [inputValue, setInputValue] = useState('');

  const selectedOption = useMemo(
    () => options.find((t) => t.id === value) ?? null,
    [options, value],
  );

  const filterOptions = useMemo(
    () =>
      createFilterOptions<SearchableTenantOption>({
        stringify: (o) => [o.name, o.subdomain, o.id].filter(Boolean).join(' '),
      }),
    [],
  );

  useEffect(() => {
    setInputValue(selectedOption?.name ?? '');
  }, [selectedOption?.id, selectedOption?.name]);

  if (!loading && options.length === 0) {
    return <Typography variant="body2" color="text.secondary">No tenants available</Typography>;
  }

  if (loading && options.length === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 200, ...sx }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading tenants…</Typography>
      </Stack>
    );
  }

  return (
    <Autocomplete
      sx={{ minWidth: 260, maxWidth: 420, flexShrink: 0, width: { xs: '100%', sm: 320 }, ...sx }}
      size="small"
      disabled={disabled}
      options={options}
      loading={loading}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_e, next, reason) => {
        if (reason === 'reset') {
          setInputValue(selectedOption?.name ?? '');
          return;
        }
        setInputValue(next);
      }}
      onChange={(_e, v) => onChange(v?.id ?? '')}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      disableClearable={options.length === 1}
      filterOptions={filterOptions}
      openOnFocus
      renderOption={
        showSubdomainInOptions
          ? (props, option) => (
            <li {...props} key={option.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{option.name}</Typography>
                {option.subdomain ? (
                  <Typography variant="caption" color="text.secondary">
                    {option.subdomain}
                  </Typography>
                ) : null}
              </Stack>
            </li>
          )
          : undefined
      }
      renderInput={(params) => (
        <AppTextField
          {...params}
          label={label}
          placeholder={
            options.length === 1 ? 'Open to confirm tenant' : 'Search by name, subdomain, or ID…'
          }
        />
      )}
    />
  );
}
